/**
 * LegalTemplates PDF Editor — assists (Phase 3 of the redline)
 *
 * Three helpers that sit on top of the document the person already has open:
 *  - Explain a clause: click a paragraph, get a plain-English reading of what
 *    it says (one short model call, cached by clause text on the server).
 *  - Check for blanks: the detected fields still empty, each one a jump.
 *  - Fill from my details (members): the profile and the answers behind
 *    earlier documents, matched to the blanks deterministically and applied
 *    only after the member ticks them.
 *
 * Every model-backed sentence stays descriptive (the server sweeps
 * directives and outcomes); nothing here ever tells the reader what to do.
 */
import { cfg, pagesEl, publicEditorIsAuthenticated, session, state } from './context.js';
import { pushHistory, redrawAllPages } from './annotations.js';
import { scheduleDraftSave } from './drafts.js';
import {
    getFormFieldDisplayValue,
    getFormFieldsForPage,
    getOrderedEditableTextFormFields,
    normalizeFormFieldName,
    openInlineFormFieldEditor,
} from './form-fill.js';
import { getApiErrorMessage, getCsrfToken } from './io.js';
import { buildPageTextLines } from './core.js';
import { buildPageTextBlocks, findTextBlockAtPoint } from './text.js';
import { activateTool, setStatus, showAlert } from './ui.js';
import { preparePage } from './viewport.js';

const els = { modal: null, title: null, body: null, footer: null, close: null };

// Diagnostics for the browser console, off unless a tab opts in with
// sessionStorage.setItem('pdfEditorDebug', '1') and reloads.
function exposeDebugHooks() {
    try {
        if (typeof window !== 'undefined' && window.sessionStorage && window.sessionStorage.getItem('pdfEditorDebug') === '1') {
            window.pdfEditorAssist = { paragraphAtPoint, labelForField, emptyTextFields, lines: buildPageTextLines, blocks: buildPageTextBlocks, fields: getFormFieldsForPage, state };
        }
    } catch (error) {
        // storage blocked: no hooks
    }
}

function assistConfig() {
    return cfg.publicEditorAssist || {};
}

function esc(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function ensureEls() {
    if (!els.modal) {
        els.modal = document.getElementById('pdf-assist-modal');
        els.title = document.getElementById('pdf-assist-title');
        els.body = document.getElementById('pdf-assist-body');
        els.footer = document.getElementById('pdf-assist-footer');
        els.close = document.getElementById('pdf-assist-close');
    }
    return Boolean(els.modal && els.body);
}

export function bindAssist() {
    exposeDebugHooks();
    if (!ensureEls()) {
        return;
    }
    if (els.close) {
        els.close.addEventListener('click', closeAssistModal);
    }
    els.modal.addEventListener('click', (event) => {
        if (event.target === els.modal) {
            closeAssistModal();
        }
    });
}

export function openAssistModal(view, title, bodyHtml, footerHtml = '') {
    if (!ensureEls()) {
        return;
    }
    els.modal.dataset.assistView = view;
    if (els.title) {
        els.title.textContent = title;
    }
    els.body.innerHTML = bodyHtml;
    if (els.footer) {
        els.footer.innerHTML = footerHtml;
        els.footer.hidden = footerHtml === '';
    }
    els.modal.classList.add('active');
    els.modal.setAttribute('aria-hidden', 'false');
    document.addEventListener('keydown', onKeydown, true);
    const focusable = els.body.querySelector('input, button, a, [tabindex]');
    if (focusable) {
        focusable.focus();
    }
}

export function closeAssistModal() {
    if (!ensureEls() || !els.modal.classList.contains('active')) {
        return;
    }
    els.modal.classList.remove('active');
    els.modal.setAttribute('aria-hidden', 'true');
    delete els.modal.dataset.assistView;
    document.removeEventListener('keydown', onKeydown, true);
}

function isAssistView(view) {
    return Boolean(ensureEls() && els.modal.classList.contains('active') && els.modal.dataset.assistView === view);
}

function onKeydown(event) {
    if (event.key === 'Escape') {
        event.stopPropagation();
        event.preventDefault();
        closeAssistModal();
    }
}

function jsonHeaders() {
    return {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': getCsrfToken(),
    };
}

// ---------------------------------------------------------------- Explain

function lineText(line) {
    if (line && typeof line.text === 'string') {
        return line.text;
    }
    return ((line && line.runs) || []).map((run) => String(run.text || '')).join('');
}

export function blockText(block) {
    return ((block && block.lines) || []).map(lineText).join(' ').replace(/\s+/g, ' ').trim();
}

/** The block under the point, else the nearest one within reach of the click. */
export function findBlockNear(pageNumber, x, y) {
    const hit = findTextBlockAtPoint(pageNumber, x, y);
    if (hit) {
        return hit;
    }
    let best = null;
    let bestDistance = Infinity;
    buildPageTextBlocks(pageNumber).forEach((block) => {
        const dx = x < block.x ? block.x - x : (x > block.right ? x - block.right : 0);
        const dy = y < block.top ? block.top - y : (y > block.bottom ? y - block.bottom : 0);
        const distance = Math.hypot(dx, dy * 1.5);
        if (distance < bestDistance) {
            bestDistance = distance;
            best = block;
        }
    });
    return bestDistance <= 48 ? best : null;
}

/** A line-item marker at the start of a line: "14 ", "7b ", "(a) ", "iv. ", a bullet. */
const ITEM_MARKER = /^\s*(?:\(?\d{1,2}[a-z]?\)?[.)]?|\(?[a-z]\)|[ivx]{1,4}[.)]|[•·▪■□☐])\s+\S/i;

function spanOverlapRatio(a, b) {
    const overlap = Math.max(0, Math.min(a.right, b.right) - Math.max(a.x, b.x));
    const narrower = Math.max(1, Math.min(a.right - a.x, b.right - b.x));
    return overlap / narrower;
}

/**
 * The paragraph under a click, built from the page's text LINES rather than
 * the Edit Text tool's blocks: on a form laid out as a grid the block
 * builder happily merges every cell on the page into one block. Starting
 * from the clicked line, neighbouring rows join while they sit within a
 * line's advance, overlap the paragraph's column, share its type size, and
 * do not start a new numbered item or a wider structure.
 *
 * @returns {{ lines: Array, text: string } | null}
 */
export function paragraphAtPoint(pageNumber, x, y) {
    const lines = buildPageTextLines(pageNumber).filter((line) => lineText(line).trim() !== '');
    if (!lines.length) {
        return null;
    }

    let clicked = null;
    let bestScore = Infinity;
    lines.forEach((line) => {
        const em = Math.max(1, Number(line.glyphHeight) || Number(line.fontSize) || 12);
        // The line's band from its baseline: ascent above, a little below.
        const top = line.baselineY - em * 0.95;
        const bottom = line.baselineY + em * 0.35;
        const dy = y < top ? top - y : (y > bottom ? y - bottom : 0);
        const dx = x < line.x ? line.x - x : (x > line.right ? x - line.right : 0);
        if (dy > Math.max(3, em * 0.6) || dx > em * 2) {
            return;
        }
        const score = dy * 2 + dx;
        if (score < bestScore) {
            bestScore = score;
            clicked = line;
        }
    });
    if (!clicked) {
        return null;
    }

    // Render units throughout: the true glyph height (never the 8px-floored
    // fontSize) and the page's own typical line advance.
    const lineEm = (line) => Math.max(1, Number(line.glyphHeight) || Number(line.fontSize) || 12);
    const em = lineEm(clicked);
    const rowTolerance = Math.max(1.5, em * 0.35);
    const rows = [];
    lines.slice().sort((a, b) => (a.baselineY - b.baselineY) || (a.x - b.x)).forEach((line) => {
        const row = rows.length ? rows[rows.length - 1] : null;
        if (row && Math.abs(line.baselineY - row.baselineY) <= rowTolerance) {
            row.lines.push(line);
        } else {
            rows.push({ baselineY: line.baselineY, lines: [line] });
        }
    });
    const clickedRow = rows.findIndex((row) => row.lines.includes(clicked));
    const span = { x: clicked.x, right: clicked.right };
    // One line advance is ~1.2× the type size; a blank line doubles it. The
    // page's median advance guards against unusual leading.
    const advances = [];
    for (let index = 1; index < rows.length; index += 1) {
        const delta = rows[index].baselineY - rows[index - 1].baselineY;
        if (delta >= em * 0.8 && delta <= em * 4) {
            advances.push(delta);
        }
    }
    advances.sort((a, b) => a - b);
    const typicalAdvance = advances.length ? advances[Math.floor(advances.length / 2)] : em * 1.2;
    const gapLimit = Math.max(em * 1.9, typicalAdvance * 1.6);
    const maxLines = 40;

    const candidateIn = (row) => {
        let best = null;
        let bestRatio = 0;
        row.lines.forEach((line) => {
            const ratio = spanOverlapRatio(span, line);
            if (ratio > bestRatio) {
                bestRatio = ratio;
                best = line;
            }
        });
        return bestRatio >= 0.5 ? best : null;
    };
    const joins = (previous, line) => Math.abs(line.baselineY - previous.baselineY) <= gapLimit
        && Math.abs(lineEm(line) - em) <= Math.max(1, em * 0.25)
        // A line starting well left of the paragraph is a wider structure (a
        // new numbered item spanning the page), not a continuation — except
        // the item's own first line, whose number hangs in the margin.
        && line.x >= span.x - em * (ITEM_MARKER.test(lineText(line)) ? 6 : 2);
    const widen = (line) => {
        span.x = Math.min(span.x, line.x);
        span.right = Math.max(span.right, line.right);
    };

    // Rows that hold only another column's text sit between this
    // paragraph's lines on a form; step over them while the vertical gap
    // still fits one line advance.
    const before = [];
    if (!ITEM_MARKER.test(lineText(clicked))) {
        let previous = clicked;
        for (let index = clickedRow - 1; index >= 0 && before.length < maxLines; index -= 1) {
            const line = candidateIn(rows[index]);
            if (!line) {
                if (previous.baselineY - rows[index].baselineY > gapLimit) {
                    break;
                }
                continue;
            }
            if (!joins(previous, line)) {
                break;
            }
            before.unshift(line);
            widen(line);
            previous = line;
            if (ITEM_MARKER.test(lineText(line))) {
                break; // the item's first line: the paragraph starts here
            }
        }
    }

    const after = [];
    let previous = clicked;
    for (let index = clickedRow + 1; index < rows.length && after.length < maxLines; index += 1) {
        const line = candidateIn(rows[index]);
        if (!line) {
            if (rows[index].baselineY - previous.baselineY > gapLimit) {
                break;
            }
            continue;
        }
        if (!joins(previous, line) || ITEM_MARKER.test(lineText(line))) {
            break;
        }
        after.push(line);
        widen(line);
        previous = line;
    }

    const selected = [...before, clicked, ...after];
    const text = selected.map(lineText).join(' ').replace(/\s+/g, ' ').trim();

    return text ? { lines: selected, text } : null;
}

export async function explainAtPoint(pageNumber, point) {
    const config = assistConfig();
    if (config.explainEnabled === false || !config.explainUrl) {
        showAlert('Clause explanations are switched off right now.', true);
        return;
    }
    const paragraph = paragraphAtPoint(pageNumber, point.x, point.y);
    state.lastExplainPoint = { pageNumber, x: point.x, y: point.y, paragraphLines: paragraph ? paragraph.lines.length : 0 };
    let text = paragraph ? paragraph.text : '';
    if (!text) {
        const block = findBlockNear(pageNumber, point.x, point.y);
        text = block ? blockText(block) : '';
    }
    const minChars = Math.max(10, Number(config.explainMinChars || 40));
    if (!text || text.length < minChars) {
        const fragments = state.pageTextFragments.get(pageNumber) || [];
        showAlert(fragments.length
            ? 'Click a paragraph with at least a sentence of text.'
            : 'No selectable text on this page. This may be a scanned PDF image.', true);
        setStatus('Explain: click a paragraph');
        return;
    }
    const clause = text.slice(0, Math.max(minChars, Number(config.explainMaxChars || 2000)));

    openAssistModal('explain', 'What this clause means', renderExplainQuote(clause)
        + '<p class="pdf-assist-status" data-assist-status="loading">Reading this clause&hellip;</p>');
    setStatus('Reading this clause…');

    try {
        const response = await fetch(config.explainUrl, {
            method: 'POST',
            headers: jsonHeaders(),
            credentials: 'same-origin',
            body: JSON.stringify({ text: clause, doc_name: session.docName || '', page: pageNumber }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(getApiErrorMessage(payload, 'The explanation is not available right now.'));
        }
        if (!isAssistView('explain')) {
            return;
        }
        els.body.innerHTML = renderExplainQuote(clause) + renderExplanation(payload);
        els.footer.hidden = false;
        els.footer.innerHTML = renderExplainFooter(payload);
        setStatus('Explanation ready');
    } catch (error) {
        if (!isAssistView('explain')) {
            return;
        }
        els.body.innerHTML = renderExplainQuote(clause)
            + `<p class="pdf-assist-error" data-assist-status="error">${esc(error.message || 'The explanation is not available right now.')}</p>`;
        setStatus('Explanation unavailable');
    }
}

function renderExplainQuote(clause) {
    return `<blockquote class="pdf-assist-quote">${esc(clause)}</blockquote>`;
}

function renderExplanation(payload) {
    const explanation = (payload && payload.explanation) || {};
    const list = (items, heading) => (Array.isArray(items) && items.length
        ? `<h3 class="pdf-assist-heading">${esc(heading)}</h3><ul class="pdf-assist-bullets">${items.map((item) => `<li>${esc(item)}</li>`).join('')}</ul>`
        : '');
    return `<p class="pdf-assist-summary">${esc(explanation.summary || '')}</p>`
        + (explanation.means ? `<p class="pdf-assist-means">${esc(explanation.means)}</p>` : '')
        + list(explanation.watch_for, 'People commonly look at')
        + list(explanation.questions, 'Questions people commonly ask');
}

function renderExplainFooter(payload) {
    const lawyer = payload && payload.ask_a_lawyer;
    return `<p class="pdf-assist-fineprint">${esc((payload && payload.disclaimer) || '')}</p>`
        + (lawyer && lawyer.url
            ? `<a class="pdf-assist-link" data-assist-lawyer href="${esc(lawyer.url)}" target="_blank" rel="noopener sponsored">${esc(lawyer.label)}</a>`
            : '');
}

// ----------------------------------------------------------------- Blanks

export function emptyTextFields() {
    return getOrderedEditableTextFormFields()
        .filter(({ field }) => String(getFormFieldDisplayValue(field) ?? '').trim() === '');
}

function nearbyLabelText(field) {
    const fragments = state.pageTextFragments.get(field.page) || [];
    const fieldTop = Number(field.y || 0);
    const fieldBottom = fieldTop + Number(field.height || 0);
    const fieldLeft = Number(field.x || 0);
    const clean = (parts) => parts.map((fragment) => String(fragment.text || '')).join(' ')
        .replace(/\s+/g, ' ')
        .replace(/(?:\s*\.){3,}\s*$/g, '') // dotted leaders
        .replace(/[:_\s]+$/g, '')
        .trim();

    // Baselines are exact render units; fragment heights are floored for
    // rendering and lie on a small page.
    const baselineOf = (fragment) => (Number.isFinite(Number(fragment.baselineY))
        ? Number(fragment.baselineY)
        : Number(fragment.y || 0) + Number(fragment.height || 0) * 0.86);
    const fieldSpanRight = fieldLeft + Number(field.width || 0);

    // "Label: ____" — text on the field's row ending close to its left edge.
    const sameRowWithin = (reach) => fragments.filter((fragment) => {
        const baseline = baselineOf(fragment);
        const right = Number(fragment.x || 0) + Number(fragment.width || 0);
        return baseline >= fieldTop - 1
            && baseline <= fieldBottom + 1
            && right <= fieldLeft + 4
            && right >= fieldLeft - reach;
    }).sort((a, b) => Number(a.x || 0) - Number(b.x || 0));
    const emOf = (fragment) => Math.max(1, Number(fragment.glyphHeight) || Number(fragment.fontSize) || 8);
    const nearest = sameRowWithin(Number.MAX_SAFE_INTEGER);
    const tightReach = nearest.length ? emOf(nearest[nearest.length - 1]) * 3 : 0;
    let text = clean(sameRowWithin(tightReach));
    // A bare item number or a leader dot beside the field is not a label:
    // boxed forms print the wording above the box, after the number.
    const isToken = (value) => value === '' || /^[\d.]{0,3}[a-z]?$/i.test(value) || /^[.\s]+$/.test(value);
    const prefix = isToken(text) && /^\d{1,2}[a-z]?$/i.test(text) ? text : '';
    if (isToken(text)) {
        text = '';
    }

    if (!text) {
        // The row of text just above the field, inside its horizontal span:
        // government forms print the label at the top of the box.
        const reach = Math.max(10, Number(field.height || 12) * 1.6);
        const above = fragments.filter((fragment) => {
            const baseline = baselineOf(fragment);
            const left = Number(fragment.x || 0);
            return baseline <= fieldTop + 1
                && fieldTop - baseline <= reach
                && left >= fieldLeft - 12
                && left <= fieldSpanRight;
        });
        if (above.length) {
            // Keep only the lowest row of that text (the label, not the row above it).
            const lowest = Math.max(...above.map(baselineOf));
            text = clean(above
                .filter((fragment) => lowest - baselineOf(fragment) <= Math.max(2, (Number(fragment.glyphHeight) || 8) * 0.5))
                .sort((a, b) => Number(a.x || 0) - Number(b.x || 0)));
        }
    }

    if (!text && nearest.length) {
        // Last resort: text further left on the same row (a wide form row).
        text = clean(sameRowWithin(emOf(nearest[nearest.length - 1]) * 14));
        if (isToken(text)) {
            text = '';
        }
    }
    if (text && prefix && !text.startsWith(prefix)) {
        text = `${prefix} ${text}`;
    }
    // A neighbouring box's number that shares the label row.
    text = text.replace(/\s+\d{1,2}[a-z]?$/i, '');

    return text.length > 72 ? `${text.slice(0, 69)}…` : text;
}

/**
 * Field names that carry no meaning: PDF-authoring tools and government
 * forms generate them (topmostSubform[0].Page1[0].f1_2[0], Text3, field_1_4).
 */
const GENERATED_FIELD_NAME = /\[\d+\]|^(?:f|c|t|tx|cb|chk|txt)\d+(?:[_\-]\d+)*$|^(?:text|check ?box|checkbox|field|untitled|widget)[\s_\-]*\d*$|topmostsubform|^page\d/i;

/**
 * A readable name for a field: the widget's tooltip, then a meaningful field
 * name, then the text beside or above it (the only clue on most government
 * forms, whose names are generated).
 */
export function labelForField(field) {
    if (!field) {
        return 'Field';
    }
    const tooltip = String(field.label || '').replace(/\s+/g, ' ').trim();
    if (tooltip) {
        return tooltip.length > 72 ? `${tooltip.slice(0, 69)}…` : tooltip;
    }
    if (!field.heuristic) {
        const raw = String(field.name || '');
        const leaf = raw.split('.').pop() || raw;
        if (raw && !GENERATED_FIELD_NAME.test(raw) && !GENERATED_FIELD_NAME.test(leaf)) {
            const words = leaf
                .replace(/[_\-.]+/g, ' ')
                .replace(/([a-z])([A-Z])/g, '$1 $2')
                .replace(/\s+/g, ' ')
                .trim();
            if (words) {
                return words.charAt(0).toUpperCase() + words.slice(1);
            }
        }
    }
    return nearbyLabelText(field) || `Field on page ${field.page}`;
}

export function openBlanksSheet() {
    if (!state.pdfJsDoc) {
        showAlert('Upload a PDF first.', true);
        return;
    }
    const blanks = emptyTextFields();
    const count = blanks.length;
    let body;
    if (!count) {
        body = '<p class="pdf-assist-lead">No blank fields found.</p>'
            + '<p class="pdf-assist-fineprint">This checks the fillable fields the editor detected. If a form has no native fields, Fill &amp; Sign → Detect Fields scans underlines and boxes first.</p>';
    } else {
        body = `<p class="pdf-assist-lead">${count} field${count === 1 ? ' is' : 's are'} still blank. Click one to fill it.</p>`
            + '<ul class="pdf-assist-list">'
            + blanks.map(({ page, field }) => `<li><button type="button" class="pdf-assist-row" data-assist-blank="${esc(field.id)}" data-assist-page="${page}">`
                + `<span class="pdf-assist-row-label">${esc(labelForField(field))}</span><span class="pdf-assist-row-meta">Page ${page}</span></button></li>`).join('')
            + '</ul>';
    }
    openAssistModal('blanks', 'Blank fields', body);
    els.body.querySelectorAll('[data-assist-blank]').forEach((button) => {
        button.addEventListener('click', () => {
            jumpToBlank(button.dataset.assistBlank, Number(button.dataset.assistPage)).catch(() => {});
        });
    });
}

async function jumpToBlank(fieldId, pageNumber) {
    closeAssistModal();
    const field = getFormFieldsForPage(pageNumber).find((candidate) => String(candidate.id) === String(fieldId));
    if (!field) {
        return;
    }
    activateTool('form-fill');
    await Promise.resolve(preparePage(pageNumber, state.renderGeneration));
    const shell = pagesEl.querySelector(`[data-page="${pageNumber}"]`);
    if (!shell) {
        return;
    }
    shell.scrollIntoView({ block: 'center' });
    state.lastActivePage = pageNumber;
    openInlineFormFieldEditor(shell, pageNumber, field);
    setStatus(`Blank field on page ${pageNumber}`);
}

/** One quiet nudge per document at export time; never blocks the download. */
export function nudgeBlanksBeforeExport() {
    if (!state.pdfJsDoc) {
        return;
    }
    const key = `${session.docId || 'doc'}:${state.pdfJsDoc.numPages || 0}`;
    if (state.blanksNudgedDocKey === key) {
        return;
    }
    const count = emptyTextFields().length;
    if (!count) {
        return;
    }
    state.blanksNudgedDocKey = key;
    showAlert(`${count} field${count === 1 ? ' is' : 's are'} still blank. Fill & Sign → Check for blanks lists them.`);
}

// ---------------------------------------------------------------- Prefill

const PROFILE_RULES = [
    { key: 'full_name', test: (label) => label === 'name' || /^(your |printed? |full |signer'?s? |legal )*name( of signer| \(print(ed)?\)| \(printed\))?$/.test(label) },
    { key: 'first_name', test: (label) => /^(your )?(first|given) name$/.test(label) },
    { key: 'last_name', test: (label) => /^(your )?(last|family|sur) ?name$/.test(label) },
    { key: 'email', test: (label) => /\be-?mail\b/.test(label) },
    { key: 'company_name', test: (label) => /^(your )?(company|business|organi[sz]ation|firm)( name)?$/.test(label) },
    { key: 'city', test: (label) => label === 'city' || label === 'city/town' || label === 'town' },
    { key: 'country', test: (label) => label === 'country' },
    { key: 'today', test: (label) => /^(today'?s )?date( signed| of signature)?$|^dated?$/.test(label) },
];

const STOP_WORDS = new Set(['the', 'a', 'an', 'of', 'and', 'or', 'for', 'to', 'your', 'please', 'enter', 'field', 'name', 'full']);

function normalizeLabel(label) {
    return String(label || '')
        .toLowerCase()
        .replace(/[^a-z0-9\s'/\-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function labelTokens(text) {
    return normalizeLabel(text).split(/[\s/\-]+/).filter((token) => token && !STOP_WORDS.has(token));
}

/**
 * Deterministic matching: a profile rule on the whole label first, then the
 * answer keys of earlier documents by word overlap. Party roles alone
 * (tenant, landlord…) never map to the member's own name — the member may
 * be the other side.
 */
export function proposeFills(fields, facts) {
    const byKey = new Map((facts || []).map((fact) => [fact.key, fact]));
    const proposals = [];
    fields.forEach(({ page, field }) => {
        const label = labelForField(field);
        const normalized = normalizeLabel(label);
        let fact = null;
        let score = 0;
        for (const rule of PROFILE_RULES) {
            if (byKey.has(rule.key) && rule.test(normalized)) {
                fact = byKey.get(rule.key);
                score = 0.95;
                break;
            }
        }
        if (!fact) {
            const tokens = new Set(labelTokens(label));
            if (tokens.size) {
                (facts || []).forEach((candidate) => {
                    if (!String(candidate.key || '').startsWith('answer:')) {
                        return;
                    }
                    const keyTokens = new Set(labelTokens(String(candidate.key).slice(7).replace(/_/g, ' ')));
                    if (!keyTokens.size) {
                        return;
                    }
                    let shared = 0;
                    keyTokens.forEach((token) => {
                        if (tokens.has(token)) {
                            shared += 1;
                        }
                    });
                    if (!shared) {
                        return;
                    }
                    const ratio = shared / Math.max(keyTokens.size, tokens.size);
                    const subset = shared === keyTokens.size || shared === tokens.size;
                    const candidateScore = subset ? Math.max(ratio, 0.7) : ratio;
                    if (candidateScore >= 0.67 && candidateScore > score) {
                        score = candidateScore;
                        fact = candidate;
                    }
                });
            }
        }
        if (fact) {
            proposals.push({ page, field, label, fact, score });
        }
    });
    return proposals;
}

function sourceLabel(fact) {
    const source = String(fact.source || '');
    if (source === 'profile') {
        return 'From your profile';
    }
    if (source === 'today') {
        return 'Today';
    }
    if (source.startsWith('document:')) {
        return `From “${source.slice(9)}”`;
    }
    return 'From your details';
}

export async function openPrefillSheet() {
    const config = assistConfig();
    if (!publicEditorIsAuthenticated || !config.factsUrl) {
        showAlert('Sign in to fill forms from your details.', true);
        return;
    }
    if (!state.pdfJsDoc) {
        showAlert('Upload a PDF first.', true);
        return;
    }
    const targets = emptyTextFields();
    if (!targets.length) {
        openAssistModal('prefill', 'Fill from my details', '<p class="pdf-assist-lead">No blank fields to fill.</p>'
            + '<p class="pdf-assist-fineprint">Only empty fields are ever filled; values you typed are never replaced.</p>');
        return;
    }
    openAssistModal('prefill', 'Fill from my details', '<p class="pdf-assist-status" data-assist-status="loading">Looking at your profile and earlier documents&hellip;</p>');

    let facts = [];
    try {
        const response = await fetch(config.factsUrl, { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(getApiErrorMessage(payload, 'Your details are not available right now.'));
        }
        facts = Array.isArray(payload.facts) ? payload.facts : [];
    } catch (error) {
        if (isAssistView('prefill')) {
            els.body.innerHTML = `<p class="pdf-assist-error">${esc(error.message)}</p>`;
        }
        return;
    }
    if (!isAssistView('prefill')) {
        return;
    }

    const proposals = proposeFills(targets, facts);
    if (!proposals.length) {
        els.body.innerHTML = '<p class="pdf-assist-lead">Nothing matched.</p>'
            + `<p class="pdf-assist-fineprint">${targets.length} blank field${targets.length === 1 ? '' : 's'} found, but none of their labels match your profile or the answers in your earlier documents.</p>`;
        return;
    }
    els.body.innerHTML = `<p class="pdf-assist-lead">${proposals.length} of ${targets.length} blank field${targets.length === 1 ? '' : 's'} match your details. Untick any you don't want.</p>`
        + '<ul class="pdf-assist-list">'
        + proposals.map((proposal, index) => '<li class="pdf-assist-proposal"><label>'
            + `<input type="checkbox" data-assist-proposal="${index}" checked>`
            + `<span class="pdf-assist-proposal-copy"><span class="pdf-assist-row-label">${esc(proposal.label)}</span>`
            + `<span class="pdf-assist-proposal-value">${esc(proposal.fact.value)}</span>`
            + `<span class="pdf-assist-row-meta">${esc(sourceLabel(proposal.fact))} · Page ${proposal.page}</span></span></label></li>`).join('')
        + '</ul>';
    els.footer.hidden = false;
    els.footer.innerHTML = `<button type="button" id="pdf-assist-apply" class="pdf-editor-action pdf-editor-action-dark">Fill ${proposals.length} field${proposals.length === 1 ? '' : 's'}</button>`
        + '<p class="pdf-assist-fineprint">Values come from your profile and the answers you gave in earlier documents. Nothing is sent to an AI model.</p>';
    const apply = document.getElementById('pdf-assist-apply');
    if (apply) {
        apply.addEventListener('click', () => applyProposals(proposals));
    }
}

function applyProposals(proposals) {
    const chosen = proposals.filter((proposal, index) => {
        const box = els.body.querySelector(`[data-assist-proposal="${index}"]`);
        return box && box.checked;
    });
    closeAssistModal();
    if (!chosen.length) {
        return;
    }
    pushHistory('prefill');
    chosen.forEach((proposal) => {
        state.formFieldOverrides.set(normalizeFormFieldName(proposal.field.name, proposal.field.id), {
            kind: 'text',
            value: String(proposal.fact.value),
        });
    });
    redrawAllPages();
    scheduleDraftSave();
    activateTool('form-fill');
    const label = `Filled ${chosen.length} field${chosen.length === 1 ? '' : 's'} from your details`;
    setStatus(label);
    showAlert(`${label}. Save Edits to keep them.`);
}
